import { Box, Typography } from '@mui/material';
import i18next from 'i18next';
import { DateTime } from 'luxon';
import { Trans, useTranslation } from 'react-i18next';

import WarningIconTransparent from '../../../../components/Icons/WarningIconTransparent';
import { BannerResponse } from '../../../../models/BannersModels';
import { WarningContainer } from './BannerFormContainerStyle';

interface BannerOverlapWarningProps {
  overlappingBanners: BannerResponse[];
}

function BannerOverlapWarning({ overlappingBanners }: BannerOverlapWarningProps) {
  const [t] = useTranslation();

  /**
   * Formats an ISO date string for display
   * @param iso
   */
  const formatDateTime = (iso: string) =>
    DateTime.fromISO(iso).setLocale(i18next.language).toFormat('d MMMM yyyy, HH:mm');

  /**
   * Return the translation key for overlapping banners
   */
  const renderShownAtOnce = () => {
    const count = overlappingBanners.length + 1;
    return count === 2
      ? t('pages.bannerOnboarding.overlapWarning.showBoth')
      : t('pages.bannerOnboarding.overlapWarning.showAll', { count });
  };

  return (
    <WarningContainer sx={{ marginTop: 0, marginBottom: '2rem' }}>
      <Box sx={{ display: 'flex', gap: '0.5rem', alignItems: 'center', padding: '1rem' }}>
        <WarningIconTransparent />
        <Typography sx={{ fontWeight: '700' }}>
          {overlappingBanners.length === 1
            ? t('pages.bannerOnboarding.overlapWarning.title')
            : t('pages.bannerOnboarding.overlapWarning.titleMultiple', { count: overlappingBanners.length })}
        </Typography>
      </Box>
      <Box sx={{ padding: '1rem', paddingTop: 0 }}>
        {overlappingBanners.map((banner) => (
          <Typography key={banner.id}>
            <Trans
              i18nKey="pages.bannerOnboarding.overlapWarning.overlappingBanner"
              values={{
                name: banner.title,
                start: formatDateTime(banner.startAt),
                end: banner.endAt
                  ? formatDateTime(banner.endAt)
                  : t('pages.bannerOnboarding.overlapWarning.manuallyStopped'),
              }}
              tOptions={{ interpolation: { escapeValue: false } }}
              components={{ bold: <strong /> }}
            />
          </Typography>
        ))}
        <Typography>{renderShownAtOnce()}</Typography>
      </Box>
    </WarningContainer>
  );
}

export default BannerOverlapWarning;
